import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { buildUrl, formatAmount } from 'utils';
import { useStyles } from './Common.styled';
import { useHistory } from 'react-router-dom';
import { Box, Typography, Grid, Paper } from '@material-ui/core';
import { Card, Table } from 'components/UI';
import { InvoiceActions } from 'state/modules/invoice';

const EntriesTable = ({ accountStatementId }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    limit: 5,
    actionable: true,
    account_statement_id: accountStatementId,
  });
  const {
    fetchingInvoices,
    invoices: data,
    pagination,
  } = useSelector((state) => state.invoice || {});
  const { localeFormat } = useSelector((state) => state.organizations);
  const currentOrganization = useSelector((state) => state.auth?.currentOrganization);
  const loading = fetchingInvoices != 'DONE';

  const columns = [
    {
      name: i18n.t('common.id'),
      selector: 'id',
      grow: 0,
      minWidth: '6rem',
      sortable: false,
    },
    {
      name: i18n.t('common.reference'),
      selector: 'description',
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.total'),
      selector: 'start_time',
      format: (row) => (row?.invoiced ? `$${formatAmount(row?.invoiced)}` : '--'),
      grow: 0,
      sortable: false,
    },
  ];

  /**
   * Get comments
   * @param {object} params
   */
  const fetchInvoices = (params) => dispatch(InvoiceActions.fetchInvoices({ ...params }));

  /**
   * Handle page change
   * @param {object} event
   * @param {number} newPage New page selected
   */
  const handlePageChange = (newPage) => {
    const newParams = { ...params, page: newPage };
    setParams(newParams);
    fetchInvoices(newParams);
  };

  /**
   * Handle limit / page size change
   * @param {object} event Value of the input
   */
  const handleChangeRowsPerPage = (event) => {
    const newParams = { ...params, limit: event.target.value };
    setParams(newParams);
    fetchInvoices(newParams);
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [];

  const handleRowClick = (row) => {
    const isBill = row?.account_customer?.holder_id === currentOrganization?.id;
    if (isBill) {
      history.push(buildUrl(`/statement-entries/${row?.id}/preview`));
    } else {
      history.push(buildUrl(`/statement-entries/${row?.id}`));
    }
  };

  const title = (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography color="textPrimary" component="span">
        <Box fontSize={14} fontWeight="fontWeightMedium" data-cy="invoice-title">
          {i18n.t('statement_entries.associated', {
            entries: pagination?.totalRows || '-',
          })}
        </Box>
      </Typography>
    </Box>
  );

  useEffect(() => {
    fetchInvoices(params);
  }, []); // eslint-disable-line

  return (
    <Grid item xs={12}>
      <Card title={title} menuItems={items} headerActionType="vertical">
        <Box className={classes.container}>
          <Paper elevation={2} className={classes.paperInner}>
            <Table
              data={data}
              columns={columns}
              loading={loading}
              page={params?.page}
              limit={params?.limit}
              handlePageChange={handlePageChange}
              totalRows={pagination?.totalRows || 0}
              handlePerRowsChange={null}
              showFirstButton={false}
              showLastButton={false}
              rowsPerPageOptions={[5]}
              handleRowClicked={handleRowClick}
            />
          </Paper>
        </Box>
      </Card>
    </Grid>
  );
};

export { EntriesTable };
